import * as angular from 'angular';
import * as d3 from 'd3';

import { ComponentDefinition, Ticket, Directory } from '../../models';
import { ITicketAttachmentService } from '../../services';
import downloadFile from '../../common/helpers/download.helper';

interface FileInTree extends File {
    depth: number;
    id: string;
    isFromRaw: boolean;
    isVisible: boolean;
}

class TicketModalFileTreeController implements angular.IComponentController {
    isDeletePending: boolean;
    isLoading: boolean;
    items: File[];
    loadingMessage: string;
    preSelect: string;
    selectedFile: Blob;
    selectedItem: FileInTree;

    readonly onRemoveRawItem: (item: FileInTree) => void;
    readonly rawItems: Directory;
    readonly ticket: Ticket;

    static $inject = [
        '$q',
        '$rootScope',
        'ticketAttachmentService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$rootScope: angular.IRootScopeService,
        private _ticketAttachmentService: ITicketAttachmentService) {
    }

    $onInit() {
        this.refreshAll();
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['rawItems']?.currentValue || changes['ticket']?.currentValue) {
            this.refreshAll();
        }
    }

    // Set identation based on depth
    style(item: FileInTree) {
        return { 'padding-left': (item.depth - 1) * 20 + 'px' };
    }

    // Refresh file tree (call to parent component method)
    private refreshAll(): angular.IPromise<void> {
        this.items = [];
        this.loadingMessage = 'Loading files tree';
        this.isLoading = true;
        const ticketId = this.ticket?.id ?? 0;
        const isFromRaw = ticketId === 0;

        const promise = isFromRaw
            ? this._$q.when(this.rawItems)
            : this._ticketAttachmentService.getTicketAttachmentsAsTree(ticketId);

        return promise
            .then(data => {
                this.items = [];
                if (data) {
                    this.buildItemTree(data, isFromRaw);
                }
            })
            .finally(() => this.isLoading = false);
    }

    // Build file tree
    private buildItemTree(data: Directory, isFromRaw?: boolean) {
        const tree = this.getTree(data);
        this.items = this.treeToItems(tree, isFromRaw);
    }

    // Take data and convert to tree
    private getTree(data: Directory): FileInTree[] {
        const hierarchy = d3.layout.tree<any>()
            .children(d => {
                let result = [];
                if (d.directories) {
                    result = result.concat(d.directories);
                }
                if (d.files) {
                    result = result.concat(d.files.map(f => {
                        return {
                            name: f.name,
                            size: f.size,
                            objectUserCollection: f.objectUserCollection
                        };
                    }));
                }
                return result;
            });
        return hierarchy(data) as FileInTree[];
    }

    // Fill in missing data to display tree correctly
    private treeToItems(tree: FileInTree[], isFromRaw?: boolean): FileInTree[] {
        tree.forEach((t, i) => {
            t.id = `id${i}`;
            t.isVisible = true;
            if (t.depth === 0) {
                t.isVisible = false;
            }
            t.isFromRaw = isFromRaw;
        });
        return tree;
    }

    // Select an item
    selectItem(item: FileInTree) {
        if (this.isDeletePending) {
            this.isDeletePending = false;
            this.deleteItem(item);
        } else {
            this.selectedItem = item;
            const ticketId = this.ticket ? this.ticket.id : 0;
            this._ticketAttachmentService
                .downloadAttachment(ticketId, item.name)
                .then(response => this.selectedFile = response)
                .finally(() => this.isLoading = false);
        }
    }

    // Delete an item
    // Selection may come after and item will be null
    // A flag isDeletePending is set to ensure that when the item will be selected,
    // the delete request will be processed
    deleteItem(item: FileInTree) {
        if (item && item.isFromRaw) {
            const index = this.rawItems.files.findIndex(file => file.name !== item.name);
            if (index >= 0) {
                this.rawItems.files.splice(index, 1);
                this.buildItemTree(this.rawItems);
            }
            this.onRemoveRawItem(item);
        } else {
            if (item) {
                const ticketId = this.ticket ? this.ticket.id : 0;
                this.loadingMessage = 'Deleting file';
                return this._ticketAttachmentService
                    .deleteAttachment(ticketId, item.name)
                    .then(() => {
                        this.selectedItem = null;
                        return this.refreshAll();
                    })
                    .finally(() => this.isLoading = false);
            } else {
                if (!item) {
                    this.isDeletePending = true;
                }
            }
        }
    }

    // Download an item by creating a fake link and simulate a click on it
    downloadItem(item: FileInTree) {
        const ticketId = this.ticket ? this.ticket.id : 0;
        this.loadingMessage = 'Downloading file';
        return this._ticketAttachmentService
            .downloadAttachment(ticketId, item.name)
            .then(response => downloadFile(item.name, response))
            .finally(() => this.isLoading = false);
    }

    humanFileSize(size: number) {
        const i = Math.floor(Math.log(size) / Math.log(1024));
        return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
    }
}

export const wtTicketModalFileTree: ComponentDefinition = {
    componentId: 'wtTicketModalFileTree',
    controller: TicketModalFileTreeController,
    template: require('./ticketModalFileTree.component.html'),
    controllerAs: 'vm',
    bindings: {
        onRemoveRawItem: '&',
        rawItems: '<',
        refreshAfterUpload: '&',
        selectedFile: '=',
        selectedItem: '=',
        ticket: '<',
    }
};